import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';

export interface InventorySettings {
  inventory_lead_time_days: number;
}

const STALE = 60 * 1000;
export const LEAD_TIME_MIN = 0;
export const LEAD_TIME_MAX = 365;

/**
 * Per-company inventory settings backed by the `companies` row. Today this is
 * just the reorder lead-time assumption (#742 / migration 20260520010000); more
 * fields will land here as inventory grows.
 */
export function useInventorySettings() {
  const { company } = useCurrentCompany();
  return useQuery({
    queryKey: ['inventory', 'settings', company?.id],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<InventorySettings> => {
      // Column not yet in generated types — regen yarn supabase:gen:types after migration deploy.
      const { data, error } = await supabase
        .from('companies')
        .select('inventory_lead_time_days' as never)
        .eq('id', company!.id)
        .single();
      if (error) throw error;
      const row = (data as unknown) as { inventory_lead_time_days: number | null } | null;
      // Belt-and-suspenders: default to 7 if the column is null (shouldn't be,
      // migration sets NOT NULL DEFAULT 7).
      return {
        inventory_lead_time_days: row?.inventory_lead_time_days ?? 7,
      };
    },
  });
}

export function useUpdateInventorySettings() {
  const { company } = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { inventory_lead_time_days: number }) => {
      if (!company?.id) throw new Error('No active company');
      if (
        !Number.isInteger(input.inventory_lead_time_days) ||
        input.inventory_lead_time_days < LEAD_TIME_MIN ||
        input.inventory_lead_time_days > LEAD_TIME_MAX
      ) {
        throw new Error(`Lead time must be a whole number between ${LEAD_TIME_MIN} and ${LEAD_TIME_MAX}.`);
      }

      // Column not yet in generated types — see note above.
      const { error } = await supabase
        .from('companies')
        .update(({ inventory_lead_time_days: input.inventory_lead_time_days } as unknown) as never)
        .eq('id', company.id);
      if (error) throw error;
    },
    onSuccess: () => {
      // Settings row, low-stock alerts, and variant detail all derive from
      // this number. Invalidate the whole inventory namespace.
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
